import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Github, X } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { supabase, getOAuthRedirectUrl } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { isNativeApp, detectNativeApp, isIOS, openInExternalBrowser } from "@/lib/platform";

interface GitHubOAuthProps {
  userId: string;
  onSuccess?: () => void;
}

export const GitHubOAuth = ({
 userId, onSuccess }: GitHubOAuthProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [username, setUsername] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    checkConnection();
  }, [userId]);

  const checkConnection = async () => {
    try {
      const { data, error } = await supabase
        .from("github_connections")
        .select("github_username")
        .eq("user_id", userId)
        .maybeSingle();

      if (data && !error) {
        setConnected(true);
        setUsername(data.github_username);
      }
    } catch (error) {
      console.error("Error checking GitHub connection:", error);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    
    try {
      // Check if we're in native iOS app
      const detection = await detectNativeApp();
      const inNativeIOS = detection.isNative && isIOS();

      const GITHUB_CLIENT_ID = "Ov23lihW9CGfEoVkTLav";
      
      // GitHub OAuth Apps only support one callback URL
      // Always use the web URL - it works for both web and mobile
      const redirectUri = `${window.location.origin}/auth`;
      
      const state = btoa(JSON.stringify({ 
        userId, 
        returnUrl: window.location.pathname,
        isGitHub: true // Flag to distinguish GitHub OAuth from Google OAuth
      }));

      const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=read:user,repo&state=${state}&prompt=consent`;

      console.log('[GitHub OAuth] Initiating OAuth flow', { 
        inNativeIOS, 
        redirectUri,
        url: githubAuthUrl
      });

      if (inNativeIOS) {
        // Native iOS: Open in external Safari browser
        console.log('[GitHub OAuth] Opening OAuth in external Safari');
        const result = await openInExternalBrowser(githubAuthUrl);
        
        if (!result.success) {
          toast.error(result.error || "Failed to open Safari. Please try again.");
          setLoading(false);
          return;
        }
        
        // Show the redirect message
        toast.info(t('notifications.toast.complete_github_auth_safari'));
      } else {
        // Web browser: Show toast and redirect in same window
        toast.info(t('notifications.toast.redirecting_to_github_for_authentication'));
        
        // Small delay to show the toast before redirect
        setTimeout(() => {
          window.location.href = githubAuthUrl;
        }, 500);
      }
    } catch (error: any) {
      console.error('[GitHub OAuth] Error initiating connection:', error);
      toast.error(t('notifications.toast.failed_to_start_github_authentication'));
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      // Delete the GitHub connection
      const { error } = await supabase.from("github_connections").delete().eq("user_id", userId);

      if (error) throw error;

      // Delete any penalties associated with github_commits metric
      const { error: penaltyError } = await supabase.rpc('delete_penalties_for_builtin_metric', {
        p_user_id: userId,
        p_metric_key: 'github_commits'
      });
      
      if (penaltyError) {
        console.error('Failed to delete penalties for github_commits:', penaltyError);
        // Don't throw - connection was deleted successfully, penalty deletion is secondary
      }

      // Remove github_commits from metric preferences
      const { data: prefs, error: prefsError } = await supabase
        .from('metric_preferences')
        .select('activity_sleep_metrics')
        .eq('user_id', userId)
        .maybeSingle();

      if (!prefsError && prefs) {
        const currentMetrics = prefs.activity_sleep_metrics || [];
        const updatedMetrics = currentMetrics.filter((k: string) => k !== 'github_commits');
        
        await supabase.from('metric_preferences').upsert({
          user_id: userId,
          activity_sleep_metrics: updatedMetrics,
        });
      }

      setConnected(false);
      setUsername("");
      toast.success(t('notifications.toast.github_disconnected_and_penalties_removed'));

      queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
      queryClient.invalidateQueries({ queryKey: ["custom-metrics"] });
      queryClient.invalidateQueries({ queryKey: ["metric-preferences"] });
      queryClient.invalidateQueries({ queryKey: ["penalties"] });
    } catch (error: any) {
      console.error("Error disconnecting GitHub:", error);
      toast.error(t('notifications.toast.failed_to_disconnect_github'));
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth callback (works for both web and native deep links)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Parse URL params from current location (works for both web and deep links)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");

      if (code && state) {
        try {
          const stateData = JSON.parse(atob(state));

          // Only process if this is a GitHub OAuth callback and for the current user
          if (stateData.isGitHub && stateData.userId === userId) {
            console.log('[GitHub OAuth] Processing callback for user:', userId);
            setLoading(true);

            // Get current session for auth token
            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) {
              throw new Error("Not authenticated");
            }

            const { data, error } = await supabase.functions.invoke("github-oauth", {
              body: { code }, // Don't send userId, it will be extracted from JWT
              headers: {
                Authorization: `Bearer ${sessionData.session.access_token}`
              }
            });

            if (error) throw error;

            if (data.success) {
              setConnected(true);
              setUsername(data.username);
              
              // Add github_commits to metric preferences so it shows on the chart
              const { data: prefs } = await supabase
                .from('metric_preferences')
                .select('activity_sleep_metrics')
                .eq('user_id', userId)
                .maybeSingle();

              const currentMetrics = prefs?.activity_sleep_metrics || [];
              if (!currentMetrics.includes('github_commits')) {
                await supabase.from('metric_preferences').upsert({
                  user_id: userId,
                  activity_sleep_metrics: [...currentMetrics, 'github_commits'],
                });
              }
              
              console.log('[GitHub OAuth] Successfully connected to GitHub as', data.username);
              toast.success(`Connected to GitHub as ${data.username}`);

              queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
              queryClient.invalidateQueries({ queryKey: ["custom-metrics"] });

              onSuccess?.();

              // Clean URL (works for both web and native)
              window.history.replaceState({}, document.title, stateData.returnUrl || "/");
            }
          }
        } catch (error: any) {
          console.error("[GitHub OAuth] Callback error:", error);
          toast.error(t('notifications.toast.failed_to_connect_github'));
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          setLoading(false);
        }
      }
    };

    handleOAuthCallback();
  }, [userId]);

  if (connected) {
    return (
      <div className="flex items-center gap-2 text-sm w-full overflow-hidden">
        <Github className="w-4 h-4 flex-shrink-0" />
        <span className="text-muted-foreground truncate min-w-0">Connected as {username}</span>
        <Button variant="ghost" size="sm" onClick={handleDisconnect} disabled={loading} className="h-6 px-2 flex-shrink-0">
          {loading ? <MiniGridLoader className="w-3 h-3" /> : <X className="w-3 h-3" />}
        </Button>
      </div>
    );
  }

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleConnect} 
      disabled={loading} 
      className="w-full gap-2 overflow-hidden text-xs sm:text-sm"
    >
      {loading ? <MiniGridLoader className="w-4 h-4 flex-shrink-0" /> : <Github className="w-4 h-4 flex-shrink-0" />}
      <span className="truncate min-w-0">{t('ui.text.connect_github')}</span>
    </Button>
  );
};
